import {useRef} from "react";
import {css, cx} from "@panda/css";

import {Button, IButtonSizePrimary} from "@pg-design/button-module";
import {HeartFillIcon, HeartIcon} from "@pg-design/icons-module";
import {TextVariantUnionType} from "@pg-design/text";
import {Text} from "@pg-design/text-module";
import {useUserDevice} from "@pg-mono/user-device";

import {hitGTMOfferAddToWishlist, IHitGTMOfferWishlistParams} from "../../offer/tracking/ecommerce_events/offer/hit_gtm_offer_add_to_wishlist";
import {hitGTMPropertyAddToWishlist, IHitGTMPropertyAddToWishlistParams} from "../../offer/tracking/ecommerce_events/property/hit_gtm_property_add_to_wishlist";
import {useAppSelector} from "../../store/hooks";
import {handleFavouritesListCheckHit} from "../../tracking/algolytics_hits/favourites_list_hit";
import {getCurrencyISOCode} from "../../utils/get_currency";
import {ViewType} from "../../view_type/ViewType";
import {useFavourites} from "../hooks/use_favourites";
import {useFavouritesTopbarExperiment} from "../hooks/use_favourites_topbar_experiment";
import {hitGtmAddToCompareClick} from "../tracking/favourite_gtm_events";
import {
    AmplitudeFavouriteLocation,
    AmplitudeFavouritePlacement,
    IAmplitudeFavouriteListContext,
    IAmplitudeFavouritesEventParams
} from "../tracking/get_amplitude_favourites_event_properties";
import {gtmFavouriteAddToFavouritesClick} from "../tracking/gtm_event_favourites_multilead";
import {trackAmplitudeAddToFavourites} from "../tracking/track_amplitude_add_to_favourites";
import {trackAmplitudeRemoveFromFavourites} from "../tracking/track_amplitude_remove_from_favourites";

interface IProps {
    offer: {id: number; address?: string | null} & IHitGTMOfferWishlistParams["offer"];
    property?: {id: number; price?: number | null; price_m2?: number | null} & IHitGTMPropertyAddToWishlistParams["property"];
    display: IButtonDisplay | IIconButtonDisplay;
    className?: string;
    dataTestId?: string;
    favouritePlacement: AmplitudeFavouritePlacement;
    favouriteLocation: AmplitudeFavouriteLocation;
    favouriteListContext?: IAmplitudeFavouriteListContext;
}

type IButtonDisplay = {type: "button"; size: "s" | "m" | "l"};
type IIconButtonDisplay = {type: "icon"; outlined?: boolean};

export function FavoriteButton(props: IProps) {
    const {offer, property, display, className, dataTestId, favouritePlacement, favouriteLocation, favouriteListContext} = props;

    /**
     * Currently it's based on favourites, because first we check this in AB as mvp.
     * In case AB's passed, move it to new implementation.
     */
    const {
        isOfferInFavourites,
        isPropertyInFavourites,
        addPropertyToFavourites,
        addOfferToFavourites,
        removePropertyFromFavourites,
        removeOfferFromFavourites,
        isTreatmentGuest
    } = useFavourites();
    const {isFavouritesTopbarTreatment} = useFavouritesTopbarExperiment();
    const {isMobile} = useUserDevice();
    const viewType = useAppSelector((state) => state.viewType.current);
    const isTreatmentPlanHeart = isFavouritesTopbarTreatment && viewType === ViewType.PROPERTY;
    const isFavouriteToggleInFlightRef = useRef(false);

    const isSubscribed = property?.id ? isPropertyInFavourites(property.id) : isOfferInFavourites(offer.id);

    const getFavouritesEventParams = (withProperty: boolean): IAmplitudeFavouritesEventParams => ({
        location: favouriteLocation,
        favouritePlacement,
        offer: {id: offer.id, name: offer.name, vendorName: offer.vendor.name, address: offer.address},
        property:
            withProperty && property
                ? {
                      id: property.id,
                      number: property.number,
                      area: property.area,
                      rooms: property.rooms,
                      floor: property.floor,
                      price: property.price,
                      pricePerSquareMeter: property.price_m2,
                      currency: getCurrencyISOCode(offer.currency)
                  }
                : null,
        listContext: favouriteListContext
    });

    const onFavouriteToggle = async () => {
        if (property?.id && isSubscribed) {
            const wasRemoved = await removePropertyFromFavourites(property.id);

            if (!wasRemoved) {
                return;
            }

            trackAmplitudeRemoveFromFavourites(getFavouritesEventParams(true));
            hitGtmAddToCompareClick(false);
            handleFavouritesListCheckHit(false, offer.id, property.id);
        } else if (!property?.id && isSubscribed) {
            const wasRemoved = await removeOfferFromFavourites(offer.id);

            if (!wasRemoved) {
                return;
            }

            trackAmplitudeRemoveFromFavourites(getFavouritesEventParams(false));
            gtmFavouriteAddToFavouritesClick(false);
            handleFavouritesListCheckHit(false, offer.id);
        } else if (property?.id && !isSubscribed) {
            const wasAdded = await addPropertyToFavourites(property.id, offer.id, undefined, favouritePlacement, favouriteLocation, favouriteListContext);

            if (!wasAdded || isTreatmentGuest) {
                return;
            }

            trackAmplitudeAddToFavourites(getFavouritesEventParams(true));
            hitGtmAddToCompareClick(true);
            hitGTMPropertyAddToWishlist({offer, property});
            handleFavouritesListCheckHit(true, offer.id, property.id);
        } else {
            //  !propertyId && !isInFavourites
            const wasAdded = await addOfferToFavourites(offer.id, favouritePlacement, favouriteLocation);

            if (!wasAdded || isTreatmentGuest) {
                return;
            }

            trackAmplitudeAddToFavourites(getFavouritesEventParams(false));
            gtmFavouriteAddToFavouritesClick(true);
            hitGTMOfferAddToWishlist({offer});
            handleFavouritesListCheckHit(true, offer.id);
        }
    };

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        if (isFavouriteToggleInFlightRef.current) {
            return;
        }

        isFavouriteToggleInFlightRef.current = true;
        void onFavouriteToggle().finally(() => {
            isFavouriteToggleInFlightRef.current = false;
        });
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const Icon = isSubscribed ? HeartFillIcon : HeartIcon;
    const favouriteIconLabel = isSubscribed ? "Usuń z ulubionych" : "Dodaj do ulubionych";
    const favouriteIconA11y = {
        "aria-label": favouriteIconLabel
    };

    if (display.type === "icon") {
        if (display.outlined) {
            return (
                <Button
                    {...favouriteIconA11y}
                    className={cx(outlinedIconButton, className)}
                    dataTestId={dataTestId}
                    iconLeft={Icon}
                    iconLeftProps={{
                        fill: isSubscribed ? "var(--colors-secondary)" : "var(--colors-gray-700)",
                        size: isMobile ? "2" : "2.4",
                        wrapperColor: "transparent",
                        wrapperSize: isMobile ? "3.6" : "2.4"
                    }}
                    onClick={handleClick}
                    onMouseDown={handleMouseDown}
                    type="button"
                    variant="transparent"
                />
            );
        }

        return (
            <Button
                {...favouriteIconA11y}
                className={cx(isTreatmentPlanHeart ? treatmentIconButton : iconButton, className)}
                dataTestId={dataTestId}
                iconLeft={isFavouritesTopbarTreatment ? Icon : HeartIcon}
                iconLeftProps={
                    isTreatmentPlanHeart
                        ? {
                              fill: isSubscribed ? "var(--colors-gray-800)" : "var(--colors-gray-700)",
                              size: "2.4",
                              wrapperColor: "transparent",
                              wrapperSize: "2.4"
                          }
                        : undefined
                }
                onClick={handleClick}
                onMouseDown={handleMouseDown}
                size="x-small"
                type="button"
                variant={
                    isTreatmentPlanHeart ? "transparent" : isFavouritesTopbarTreatment ? "filled_primary" : isSubscribed ? "filled_secondary" : "filled_primary"
                }
            />
        );
    }

    return (
        <Button
            className={cx(subscribeButton, className)}
            iconLeft={HeartIcon}
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            size={getButtonSize(display.size)}
            variant={isSubscribed ? "filled_secondary" : "outlined_secondary"}
        >
            <Text variant={getButtonTextSize(display.size)}>{isSubscribed ? "W ulubionych" : "Dodaj do ulubionych"}</Text>
        </Button>
    );
}

// Styles
const subscribeButton = css({
    flex: "1 0 auto"
});

const iconButton = css({
    flex: "1 0 auto",
    width: "40px",
    minWidth: "unset"
});

const treatmentIconButton = css({
    flex: "0 0 auto",
    minWidth: "unset",
    width: "auto",
    p: 0
});

const outlinedIconButton = css({
    flex: "0 0 auto",
    w: "3.6rem",
    h: "3.6rem",
    p: 0,
    borderRadius: "50%",
    borderColor: "var(--colors-gray-400)",
    md: {
        w: "5.6rem",
        h: "5.6rem"
    },
    _hover: {
        borderColor: "var(--colors-gray-400)"
    }
});

// Utils
function getButtonSize(size: IButtonDisplay["size"]): IButtonSizePrimary {
    if (size === "m") {
        return "small";
    }

    if (size === "l") {
        return "medium";
    }

    return "x-small";
}

function getButtonTextSize(size: IButtonDisplay["size"]): TextVariantUnionType {
    if (size === "m") {
        return "button_small";
    }

    if (size === "l") {
        return "button_medium";
    }

    return "button_small";
}
import {useRef} from "react";
import {css, cx} from "@panda/css";

import {Button, IButtonSizePrimary} from "@pg-design/button-module";
import {HeartFillIcon, HeartIcon} from "@pg-design/icons-module";
import {TextVariantUnionType} from "@pg-design/text";
import {Text} from "@pg-design/text-module";
import {useUserDevice} from "@pg-mono/user-device";

import {hitGTMOfferAddToWishlist, IHitGTMOfferWishlistParams} from "../../offer/tracking/ecommerce_events/offer/hit_gtm_offer_add_to_wishlist";
import {hitGTMPropertyAddToWishlist, IHitGTMPropertyAddToWishlistParams} from "../../offer/tracking/ecommerce_events/property/hit_gtm_property_add_to_wishlist";
import {useAppSelector} from "../../store/hooks";
import {handleFavouritesListCheckHit} from "../../tracking/algolytics_hits/favourites_list_hit";
import {getCurrencyISOCode} from "../../utils/get_currency";
import {ViewType} from "../../view_type/ViewType";
import {useFavourites} from "../hooks/use_favourites";
import {useFavouritesTopbarExperiment} from "../hooks/use_favourites_topbar_experiment";
import {hitGtmAddToCompareClick} from "../tracking/favourite_gtm_events";
import {
    AmplitudeFavouriteLocation,
    AmplitudeFavouritePlacement,
    IAmplitudeFavouriteListContext,
    IAmplitudeFavouritesEventParams
} from "../tracking/get_amplitude_favourites_event_properties";
import {gtmFavouriteAddToFavouritesClick} from "../tracking/gtm_event_favourites_multilead";
import {trackAmplitudeAddToFavourites} from "../tracking/track_amplitude_add_to_favourites";
import {trackAmplitudeRemoveFromFavourites} from "../tracking/track_amplitude_remove_from_favourites";

interface IProps {
    offer: {id: number; address?: string | null} & IHitGTMOfferWishlistParams["offer"];
    property?: {id: number; price?: number | null; price_m2?: number | null} & IHitGTMPropertyAddToWishlistParams["property"];
    display: IButtonDisplay | IIconButtonDisplay;
    className?: string;
    dataTestId?: string;
    favouritePlacement: AmplitudeFavouritePlacement;
    favouriteLocation: AmplitudeFavouriteLocation;
    favouriteListContext?: IAmplitudeFavouriteListContext;
}

type IButtonDisplay = {type: "button"; size: "s" | "m" | "l"};
type IIconButtonDisplay = {type: "icon"; outlined?: boolean};

export function FavoriteButton(props: IProps) {
    const {offer, property, display, className, dataTestId, favouritePlacement, favouriteLocation, favouriteListContext} = props;

    /**
     * Currently it's based on favourites, because first we check this in AB as mvp.
     * In case AB's passed, move it to new implementation.
     */
    const {
        isOfferInFavourites,
        isPropertyInFavourites,
        addPropertyToFavourites,
        addOfferToFavourites,
        removePropertyFromFavourites,
        removeOfferFromFavourites,
        isTreatmentGuest
    } = useFavourites();
    const {isFavouritesTopbarTreatment} = useFavouritesTopbarExperiment();
    const {isMobile} = useUserDevice();
    const viewType = useAppSelector((state) => state.viewType.current);
    const isTreatmentPlanHeart = isFavouritesTopbarTreatment && viewType === ViewType.PROPERTY;
    const isFavouriteToggleInFlightRef = useRef(false);

    const isSubscribed = property?.id ? isPropertyInFavourites(property.id) : isOfferInFavourites(offer.id);

    const getFavouritesEventParams = (withProperty: boolean): IAmplitudeFavouritesEventParams => ({
        location: favouriteLocation,
        favouritePlacement,
        offer: {id: offer.id, name: offer.name, vendorName: offer.vendor.name, address: offer.address},
        property:
            withProperty && property
                ? {
                      id: property.id,
                      number: property.number,
                      area: property.area,
                      rooms: property.rooms,
                      floor: property.floor,
                      price: property.price,
                      pricePerSquareMeter: property.price_m2,
                      currency: getCurrencyISOCode(offer.currency)
                  }
                : null,
        listContext: favouriteListContext
    });

    const onFavouriteToggle = async () => {
        if (property?.id && isSubscribed) {
            const wasRemoved = await removePropertyFromFavourites(property.id);

            if (!wasRemoved) {
                return;
            }

            trackAmplitudeRemoveFromFavourites(getFavouritesEventParams(true));
            hitGtmAddToCompareClick(false);
            handleFavouritesListCheckHit(false, offer.id, property.id);
        } else if (!property?.id && isSubscribed) {
            const wasRemoved = await removeOfferFromFavourites(offer.id);

            if (!wasRemoved) {
                return;
            }

            trackAmplitudeRemoveFromFavourites(getFavouritesEventParams(false));
            gtmFavouriteAddToFavouritesClick(false);
            handleFavouritesListCheckHit(false, offer.id);
        } else if (property?.id && !isSubscribed) {
            const wasAdded = await addPropertyToFavourites(property.id, offer.id, undefined, favouritePlacement, favouriteLocation, favouriteListContext);

            if (!wasAdded || isTreatmentGuest) {
                return;
            }

            trackAmplitudeAddToFavourites(getFavouritesEventParams(true));
            hitGtmAddToCompareClick(true);
            hitGTMPropertyAddToWishlist({offer, property});
            handleFavouritesListCheckHit(true, offer.id, property.id);
        } else {
            //  !propertyId && !isInFavourites
            const wasAdded = await addOfferToFavourites(offer.id, favouritePlacement, favouriteLocation);

            if (!wasAdded || isTreatmentGuest) {
                return;
            }

            trackAmplitudeAddToFavourites(getFavouritesEventParams(false));
            gtmFavouriteAddToFavouritesClick(true);
            hitGTMOfferAddToWishlist({offer});
            handleFavouritesListCheckHit(true, offer.id);
        }
    };

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        if (isFavouriteToggleInFlightRef.current) {
            return;
        }

        isFavouriteToggleInFlightRef.current = true;
        void onFavouriteToggle().finally(() => {
            isFavouriteToggleInFlightRef.current = false;
        });
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const Icon = isSubscribed ? HeartFillIcon : HeartIcon;
    const favouriteIconLabel = isSubscribed ? "Usuń z ulubionych" : "Dodaj do ulubionych";
    const favouriteIconA11y = {
        "aria-label": favouriteIconLabel
    };

    if (display.type === "icon") {
        if (display.outlined) {
            return (
                <Button
                    {...favouriteIconA11y}
                    className={cx(outlinedIconButton, className)}
                    dataTestId={dataTestId}
                    iconLeft={Icon}
                    iconLeftProps={{
                        fill: isSubscribed ? "var(--colors-secondary)" : "var(--colors-gray-700)",
                        size: isMobile ? "2" : "2.4",
                        wrapperColor: "transparent",
                        wrapperSize: isMobile ? "3.6" : "2.4"
                    }}
                    onClick={handleClick}
                    onMouseDown={handleMouseDown}
                    type="button"
                    variant="transparent"
                />
            );
        }

        return (
            <Button
                {...favouriteIconA11y}
                className={cx(isTreatmentPlanHeart ? treatmentIconButton : iconButton, className)}
                dataTestId={dataTestId}
                iconLeft={isFavouritesTopbarTreatment ? Icon : HeartIcon}
                iconLeftProps={
                    isTreatmentPlanHeart
                        ? {
                              fill: isSubscribed ? "var(--colors-gray-800)" : "var(--colors-gray-700)",
                              size: "2.4",
                              wrapperColor: "transparent",
                              wrapperSize: "2.4"
                          }
                        : undefined
                }
                onClick={handleClick}
                onMouseDown={handleMouseDown}
                size="x-small"
                type="button"
                variant={
                    isTreatmentPlanHeart ? "transparent" : isFavouritesTopbarTreatment ? "filled_primary" : isSubscribed ? "filled_secondary" : "filled_primary"
                }
            />
        );
    }

    return (
        <Button
            className={cx(subscribeButton, className)}
            iconLeft={HeartIcon}
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            size={getButtonSize(display.size)}
            variant={isSubscribed ? "filled_secondary" : "outlined_secondary"}
        >
            <Text variant={getButtonTextSize(display.size)}>{isSubscribed ? "W ulubionych" : "Dodaj do ulubionych"}</Text>
        </Button>
    );
}

// Styles
const subscribeButton = css({
    flex: "1 0 auto"
});

const iconButton = css({
    flex: "1 0 auto",
    width: "40px",
    minWidth: "unset"
});

const treatmentIconButton = css({
    flex: "0 0 auto",
    minWidth: "unset",
    width: "auto",
    p: 0
});

const outlinedIconButton = css({
    flex: "0 0 auto",
    w: "3.6rem",
    h: "3.6rem",
    p: 0,
    borderRadius: "50%",
    borderColor: "var(--colors-gray-400)",
    md: {
        w: "5.6rem",
        h: "5.6rem"
    },
    _hover: {
        borderColor: "var(--colors-gray-400)"
    }
});

// Utils
function getButtonSize(size: IButtonDisplay["size"]): IButtonSizePrimary {
    if (size === "m") {
        return "small";
    }

    if (size === "l") {
        return "medium";
    }

    return "x-small";
}

function getButtonTextSize(size: IButtonDisplay["size"]): TextVariantUnionType {
    if (size === "m") {
        return "button_small";
    }

    if (size === "l") {
        return "button_medium";
    }

    return "button_small";
}
